import { useState, useMemo } from 'react';
import { TweetTableData } from '../hooks/useAllTweets'
import { Pagination } from './Pagination';

interface VisualTweetFeedProps {
  tweets: TweetTableData[]
  isLoading: boolean
  totalCount?: number
  searchQuery?: string
  showRecentOnly?: boolean
  showTenMinOnly: boolean
}

// Function to remove media URLs from tweet text
function removeMediaUrls(text: string): string {
  // Remove various Twitter/X media URL patterns
  return text
    .replace(/https:\/\/t\.co\/\w+/g, '') // t.co URLs
    .replace(/https:\/\/x\.com\/\w+\/status\/\d+\/photo\/\d+/g, '') // X photo URLs
    .replace(/https:\/\/twitter\.com\/\w+\/status\/\d+\/photo\/\d+/g, '') // Twitter photo URLs
    .replace(/https:\/\/x\.com\/\w+\/status\/\d+\/video\/\d+/g, '') // X video URLs
    .replace(/https:\/\/twitter\.com\/\w+\/status\/\d+\/video\/\d+/g, '') // Twitter video URLs
    .trim()
    .replace(/\s+/g, ' '); // Clean up extra whitespace
}

export function VisualTweetFeed({ 
  tweets, 
  isLoading, 
  totalCount, 
  searchQuery, 
  showRecentOnly,
  showTenMinOnly
}: VisualTweetFeedProps) {
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Reset to first page when tweets change
  useMemo(() => {
    setCurrentPage(1);
  }, [tweets.length, searchQuery, showRecentOnly, showTenMinOnly]);

  // Calculate pagination
  const totalPages = Math.ceil(tweets.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTweets = tweets.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of tweet feed
    document.querySelector('.visual-tweet-feed')?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };
  
  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat('sr-RS', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  // Get tweet URL
  const getTweetUrl = (username: string, tweetId: string) => {
    return `https://twitter.com/${username}/status/${tweetId}`
  }

  // Get author URL
  const getAuthorUrl = (username: string) => {
    return `https://twitter.com/${username}`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-500">Učitavanje tvitova...</div>
        </div>
      </div>
    )
  }

  if (totalCount === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Nema pronađenih tvitova</div>
      </div>
    )
  }

  if (tweets.length === 0) {
    return (
      <div className="visual-tweet-feed mx-auto px-4">
        <div className="text-center py-8 text-gray-500">
          Nema rezultata za trenutne filtere
        </div>
        <div className="mt-6">
          <Pagination
            currentPage={1}
            totalPages={1}
            pageSize={pageSize}
            totalItems={0}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="visual-tweet-feed mx-auto px-4 max-w-4xl">
      {/* Top pagination */}
      <div className="mb-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={tweets.length}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      {/* Results count */}
      {(searchQuery || showRecentOnly || showTenMinOnly) && (
        <div className="mb-3 text-sm text-gray-600">
          Pronađeno {totalPages > 0 ? `${Math.min(startIndex + pageSize, tweets.length)} od ${tweets.length}` : '0'} tvitova
          {searchQuery && ` (pretraga: "${searchQuery}")`}
          {showTenMinOnly && ' (poslednih 10 minuta)'}
          {showRecentOnly && !showTenMinOnly && ' (poslednih 30 minuta)'}
        </div>
      )}

      {/* Tweet Cards */}
      <div className="space-y-3">
        {paginatedTweets.map((tweet) => (
          <div 
            key={tweet.id} 
            className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden"
          >
            {/* Promoted tweet banner */}
            {tweet.is_promoted && (
              <div className="bg-blue-50 border-b border-blue-200 px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-blue-600">📢</span>
                  <span className="text-gray-700">
                    tagovao{' '}
                    <a 
                      href={`https://twitter.com/${tweet.promoted_by_username}`}
                      className="font-medium text-blue-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      @{tweet.promoted_by_username}
                    </a>
                  </span>
                  {tweet.promoted_at && (
                    <>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-500">
                        {formatDate(tweet.promoted_at)}
                      </span>
                    </>
                  )}
                </div>
                {tweet.original_author_username && tweet.original_author_username !== tweet.author_username && (
                  <div className="text-xs text-gray-600 mt-1">
                    Originalni tvit od @{tweet.original_author_username}
                  </div>
                )}
              </div>
            )}
            
            {/* Author Header */}
            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div>
                  <a
                    href={getAuthorUrl(tweet.author_username)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-gray-900 hover:text-blue-600 transition-colors text-sm"
                    onClick={(e) => e.stopPropagation()} // Prevent tweet click when clicking username
                  >
                    @{tweet.author_username}
                  </a>
                  {tweet.author_name && tweet.author_name !== tweet.author_username && (
                    <div className="text-xs text-gray-500">
                      {tweet.author_name}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {tweet.is_reply && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <span className="text-blue-500">↩</span>
                    Odgovor
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  {formatDate(tweet.created_at)}
                </span>
              </div>
            </div>

            {/* Tweet Content - clickable area with image on right */}
            <a
              href={getTweetUrl(tweet.author_username, tweet.tweet_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="block hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="px-3 py-2 flex gap-3">
                {/* Text content */}
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 whitespace-pre-wrap break-words text-sm mb-2">
                    {tweet.media_urls && Array.isArray(tweet.media_urls) && tweet.media_urls.length > 0 
                      ? removeMediaUrls(tweet.text) 
                      : tweet.text}
                  </p>
                  
                  {/* Hashtags */}
                  {tweet.hashtags && Array.isArray(tweet.hashtags) && tweet.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(() => {
                        try {
                          return tweet.hashtags.map((hashtag: any, i: number) => (
                          <span
                            key={i}
                            className="inline-block px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full"
                          >
                            #{hashtag}
                          </span>
                        ));
                        } catch (e) {
                          console.error('Error mapping hashtags:', e, tweet.hashtags);
                          return null;
                        }
                      })()}
                    </div>
                  )}
                  
                  {/* Engagement metrics */}
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    <span className={`flex items-center gap-1 ${tweet.like_count >= 20 ? 'font-semibold text-red-600' : ''}`}>
                      ❤️ {tweet.like_count.toLocaleString('sr-RS')}
                    </span>
                    <span className="flex items-center gap-1">
                      🔄 {tweet.retweet_count.toLocaleString('sr-RS')}
                    </span>
                    <span className="flex items-center gap-1">
                      💬 {tweet.reply_count.toLocaleString('sr-RS')}
                    </span>
                  </div>
                </div>

                {/* Media Preview - on the right */}
                {tweet.media_urls && Array.isArray(tweet.media_urls) && tweet.media_urls.length > 0 && (
                  <div className="flex-shrink-0 w-32 sm:w-40">
                    {(() => {
                      try {
                        // Just show the first media item for clean layout
                        const media = tweet.media_urls[0];
                        let mediaUrl: string;
                        let mediaType: string = 'photo';
                        
                        if (typeof media === 'string') {
                          mediaUrl = media;
                          if (media.includes('.mp4') || media.includes('.mov') || media.includes('/video/')) {
                            mediaType = 'video';
                          }
                        } else if (media && typeof media === 'object') {
                          mediaUrl = media.url;
                          mediaType = media.type || 'photo';
                        } else {
                          return null;
                        }
                        
                        if (!mediaUrl) return null;
                        
                        if (mediaType === 'photo' || mediaType === 'image') {
                          return (
                            <div className="relative rounded-lg overflow-hidden bg-gray-100 h-24 sm:h-28">
                              <img 
                                src={mediaUrl} 
                                alt="Tweet media"
                                className="w-full h-full object-contain"
                                loading="lazy"
                                onError={(e) => {
                                  console.error('Failed to load image:', mediaUrl);
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                              {tweet.media_urls.length > 1 && (
                                <div className="absolute top-1 right-1 bg-black bg-opacity-60 text-white text-xs px-1.5 py-0.5 rounded">
                                  +{tweet.media_urls.length - 1}
                                </div>
                              )}
                            </div>
                          );
                        } else if (mediaType === 'video' || mediaType === 'animated_gif') {
                          return (
                            <div className="relative rounded-lg overflow-hidden bg-gray-900 h-24 sm:h-28">
                              <video 
                                src={mediaUrl} 
                                className="w-full h-full object-contain"
                                preload="metadata"
                              >
                                <source src={mediaUrl} type="video/mp4" />
                              </video>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="bg-black bg-opacity-60 rounded-full p-2">
                                  <div className="w-6 h-6 border-l-8 border-l-white border-y-6 border-y-transparent border-r-0 ml-1"></div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      } catch (e) {
                        console.error('Error rendering media:', e, tweet.media_urls);
                        return null;
                      }
                    })()}
                  </div>
                )}
              </div>
            </a>
          </div>
        ))}
      </div>

      {/* Bottom pagination */}
      <div className="mt-6 mb-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={tweets.length}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  )
} 